import { useCallback, useMemo, useRef } from "react";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import { Button, ButtonProps, CircularProgress } from "@mui/material";

import useGetVideoInfo from "../../../hooks/useGetVideoInfo";
import formatTime from "../../../utils/formatTime";
import { useAppContext } from "../../../hooks/useAppContext";

// 1. Import the Autonomous Brain
import { useKernel } from "../../../hooks/AIKernel"; // Adjust relative path as needed

const VideoPlayPausebutton: React.FC<ButtonProps> = (props) => {
  const { videoRef, isProcessing } = useAppContext();
  const { currentTime, isPaused } = useGetVideoInfo();

  // 2. Connect to the Agent's state and sensory inputs
  const { agentState, reportBehavior } = useKernel();
  
  // Ref to track rapid play/pause toggles
  const clickTimestamps = useRef<number[]>([]);

  const handleVideoPlayPause = useCallback(() => {
    if (!videoRef) return;

    // 3. TELEMETRY: Track play/pause button micro-frustrations and intent
    if (reportBehavior) {
      const now = Date.now();
      clickTimestamps.current.push(now);
      
      // Keep only clicks from the last 2 seconds
      clickTimestamps.current = clickTimestamps.current.filter(t => now - t < 2000);

      if (clickTimestamps.current.length > 4) {
        // TELEMETRY: User is rapidly clicking play/pause (Frustration)
        reportBehavior("play_button_frustration", 2, "behavior");
        clickTimestamps.current = []; // Reset after reporting
      } else {
        // TELEMETRY: Standard playback intent
        reportBehavior(videoRef.paused ? "video_resumed" : "video_paused", 1, "interest");
      }
    }

    videoRef.paused ? videoRef.play() : videoRef.pause();
  }, [videoRef, reportBehavior]);

  const buttonIcon = useMemo(() => {
    if (isProcessing) {
      return <CircularProgress size={14} color="inherit" />;
    }

    if (isPaused) {
      return <PlayArrowIcon />;
    }

    return <PauseIcon />;
  }, [isPaused, isProcessing]);

  // 4. Adaptive Theme Variables
  const isDarkMode = agentState?.theme === "theme-cinematic-dark";
  const highlightCTA = agentState?.ui?.highlightCTAs;

  return (
    <Button
      {...props}
      disabled={isProcessing}
      size="small"
      variant="contained"
      startIcon={buttonIcon}
      onClick={handleVideoPlayPause}
      sx={{
        textTransform: "none",
        minWidth: "90px", // Prevent button width from jumping aggressively when time formatting changes
        justifyContent: "flex-start",
        // ADAPTIVE UI: Smooth theme transitions for Cinematic Mode
        backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.1)" : "primary.main",
        color: isDarkMode ? "#00e5ff" : "#fff",
        transition: "background-color 0.3s ease, color 0.3s ease, box-shadow 0.3s ease",
        "&:hover": {
          backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.2)" : "primary.dark",
        },
        // ADAPTIVE UI: Pulse if video is paused, user is frustrated, and they need a hint to continue
        animation: highlightCTA && isPaused && !isProcessing ? "pulse-play 2s infinite" : "none",
        "@keyframes pulse-play": {
          "0%": { boxShadow: "0 0 0 0 rgba(0, 229, 255, 0.5)" },
          "70%": { boxShadow: "0 0 0 8px rgba(0, 229, 255, 0)" },
          "100%": { boxShadow: "0 0 0 0 rgba(0, 229, 255, 0)" }
        },
        ...(props.sx || {}) // Safely spread any incoming sx props from parents
      }}
    >
      {formatTime(currentTime)}
    </Button>
  );
};

export default VideoPlayPausebutton;